import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, CheckCheck, Trash2, MapPin } from "lucide-react";

const bandStyle = (band) =>
  band === "vert" ? "bg-emerald-100 text-emerald-800"
    : band === "orange" ? "bg-amber-100 text-amber-800"
      : "bg-slate-100 text-slate-600";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
};

export default function NotificationCenter({ tone = "dark" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);

  const loadCount = useCallback(() => {
    api.get("/radar/notifications/unread-count").then((r) => setCount(r.data.count || 0)).catch(() => {});
  }, []);
  const loadItems = useCallback(() => {
    api.get("/radar/notifications", { params: { limit: 50 } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const markRead = async (id) => {
    await api.post(`/radar/notifications/${id}/read`).catch(() => {});
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    loadCount();
  };
  const markAll = async () => {
    await api.post("/radar/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setCount(0);
  };
  const remove = async (id) => {
    await api.delete(`/radar/notifications/${id}`).catch(() => {});
    setItems((prev) => prev.filter((n) => n.id !== id));
    loadCount();
  };
  const openNotif = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    navigate("/radar");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell-btn"
          className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-colors ${tone === "dark" ? "text-slate-300 hover:bg-white/10 hover:text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]"}`}
          aria-label="Notifications"
        >
          <Bell className="h-[19px] w-[19px]" strokeWidth={1.8} />
          {count > 0 && (
            <span
              data-testid="notif-unread-badge"
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#C6A87C] px-1 text-[11px] font-bold text-[#0F172A]"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[360px] p-0"
        data-testid="notif-panel"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-display text-base text-[#0F172A]">Alertes RADAR</span>
            {count > 0 && <span className="rounded-full bg-[#C6A87C] px-2 py-0.5 text-[10px] font-bold text-[#0F172A]">{count}</span>}
          </div>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notif-mark-all" className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#0F172A]">
              <CheckCheck className="h-3.5 w-3.5" /> Tout marquer lu
            </button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notif-empty">
              Aucune alerte pour le moment.
            </div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  className={`group cursor-pointer px-4 py-3 transition-colors hover:bg-slate-50 ${n.read ? "opacity-70" : "bg-[#C6A87C]/5"}`}
                  onClick={() => openNotif(n)}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#0F172A]">
                      {n.photo ? <img src={n.photo} alt="" className="h-full w-full object-cover" /> : (
                        <div className="flex h-full items-center justify-center"><MapPin className="h-5 w-5 text-[#C6A87C]/50" /></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${bandStyle(n.band)}`}>{n.score}/100</span>
                        {!n.read && <span className="h-2 w-2 rounded-full bg-[#C6A87C]" />}
                        {n.emailed && <span className="text-[10px] text-emerald-600">✉ e-mail envoyé</span>}
                      </div>
                      <p className="mt-1 truncate text-sm font-medium text-[#0F172A]">{n.title || "Bien détecté"}</p>
                      <p className="truncate text-xs text-slate-500">
                        {[n.city, n.price ? `${Number(n.price).toLocaleString("fr-FR")} €` : null].filter(Boolean).join(" · ")}
                      </p>
                      <p className="mt-0.5 text-[11px] text-slate-400">{n.search_name ? `${n.search_name} · ` : ""}{timeAgo(n.created_at)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                      data-testid={`notif-del-${n.id}`}
                      className="text-slate-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
